import * as React from 'react';
import { EDITOR_COLOR_PALETTE } from '../../../../utility';
import { EditorUtils } from '@progress/kendo-react-editor';
import {
  DropDownList as KendoDropDownList,
  DropDownListChangeEvent,
  ListItemProps
} from '@progress/kendo-react-dropdowns';
import Container from '../../../layout/container/Container';
import { classNames } from '@progress/kendo-react-common';
import { PUZZLE_FIELD_STATE } from '../Types';
import { PUZZLE_COLOR_PICKER_PROPS } from './Types';
import Field from '../../../layout/field/Field';
import { Label } from '@progress/kendo-react-labels';
import { Component } from '../../../../dispatcher/modules';
import uuid from 'uuid-random';

/**
 * This component is created as visual presentation of colorpicker
 * Component can be used in RichTextEditor as a tool and as a field which holds values of selected color from dropdown
 * @author Aleksandar Cosic / Stevan Stojanovic
 * */
export default class PuzzleColorPicker extends Component<PUZZLE_COLOR_PICKER_PROPS, PUZZLE_FIELD_STATE> {
  colorPickerID: any = null;
  dropDownRef: any;

  static defaultProps = {
    size: 'medium',
    componentType: 'colorPicker',
    /**
     * Use rgb color code as value. If false then use color name (red, blue,...) as value
     */
    valueFieldRgb: true
  }

  /**
   *@inheritDoce
   */
  moduleDidMount() {
    this.colorPickerID = this.props.id ? this.props.id : this.dropDownRef?.base?.guid;
  }

  /**
   * On onBlur event handler
   * @param event
   */
  onBlur = (event: any) => {
    const { onBlur, valueFieldRgb } = this.props;

    let color;
    if (valueFieldRgb) {
      color = this.getColorObject(event.value)[0];
    } else {
      color = event.value;
    }

    const e = {
      ...event,
      value: color
    };
    if (onBlur) {
      onBlur(e);
    }
  }

  /**
   * On change event handler
   * @param event
   */
  private onChange = (event: DropDownListChangeEvent) => {
    const { onChange, valueFieldRgb } = this.props;
    let color;
    if (valueFieldRgb) {
      color = this.getColorObject(event.value)[0];
    } else {
      color = event.value;
    }

    if (color) {
      if (onChange) {
        event.value = color;
        onChange(event);
      } else {
        const { view } = this.props;
        if (view) {
          EditorUtils.applyInlineStyle(view, {
            style: 'color',
            value: this.getColorObject(event.value)[0]
          });
        }
      }
    }
  };

  /**
   * Gets color pair ['rgb_value','color_name']
   * @param value
   */
  private getColorObject = (value: string) => {
    const { palette = EDITOR_COLOR_PALETTE } = this.props;
    return Object.entries(palette).find(element => element[1] === value);
  };

  /**
   * Item Render
   * @param li
   * @param itemProps
   */
  private itemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
    const { dataItem } = itemProps;
    const colorObject = this.getColorObject(dataItem);
    const localizedItem = typeof dataItem === 'string' && this.translate(dataItem);
    const itemChildren = (
      <Container
        key={uuid()}
        alignItemsCenter
        hBox
      >
        <span
          className={`tp-custom-color-picker-selected-color tp-round ${colorObject && colorObject[1] === 'no_color' ? 'tp-no-color' : ''}`}
          style={{ backgroundColor: colorObject && colorObject[0] }}
        />
        {localizedItem}
      </Container>
    );

    return React.cloneElement(li, li.props, [itemChildren]);
  };

  /**
   *  Value field render
   * @param element
   * @param value
   */
  private valueRender = (element: React.ReactElement<HTMLSpanElement>, value: string) => {
    const { look } = this.props;
    const colorObject = this.getColorObject(value);

    if (!colorObject) return element;

    let render;
    if (look === 'circle') {
      render = (
        <Container
          htmlTag={'span'}
          displayInlineFlex
          aria-label={`${value}`}
          className={`tp-color-text-holder circle tp-round ${colorObject[1] === 'no_color' ? 'tp-no-color' : ''}`}
          role={'figure'}
          style={{ backgroundColor: colorObject[0] }}
        />
      );
    } else {
      render = (
        <div
          aria-label={`${value}`}
          className="tp-custom-drop-down-value"
          role={'figure'}
        >
          <i className="tp-icon tp-color-text" aria-hidden="true" />
          <span
            className="tp-color-text-holder"
            style={{ backgroundColor: colorObject[0] }}
          />
        </div>
      );
    }

    return React.cloneElement(element, { ...element.props }, render);
  };

  createHandler = (eventName, state) => (event: any) => {
    this.setState(state);
  };

  render() {
    let {
      asField,
      ariaDescribedBy,
      ariaLabelledBy,
      className = '',
      defaultValue,
      value,
      errorTxt,
      name,
      popupSettings,
      palette = EDITOR_COLOR_PALETTE,
      label,
      labelClassName = '',
      look,
      size,
      valid,
      disabled,
      fieldClassName = '',
      fieldMandatory,
      fieldWithAdditionalEl,
      withValidation,
      dataCy
    } = this.props;

    const paletteArray = Object.entries(palette).reduce((array: string[], item: any) => array.concat(item[1]), []);

    if (look === 'circle' && defaultValue) {
      defaultValue = palette[defaultValue];
    }

    const fieldCls = classNames({
      [className]: className,
      'k-medium': size === 'medium',
      'k-state-empty': defaultValue === 'no_color',
      'k-state-invalid': valid === false,
      'k-state-disabled': disabled
    }
    );

    const asFieldProps = {
      fieldClassName,
      fieldMandatory,
      fieldWithAdditionalEl
    };

    const colorPickerProps = {
      componentType: this.props.componentType
    };

    const content = (
      <div
        data-cy={dataCy ? `${dataCy}-wrapper` : undefined}
        className={`k-d-flex-col ${fieldCls}`}
      >
        {label && <Label
          editorId={this.colorPickerID}
          className={classNames({
            [labelClassName]: labelClassName,
            'k-mandatory': !asField && fieldMandatory
          })}
        >
          {label}
        </Label>}
        <KendoDropDownList
          ariaLabel={this.translate('SRL-colorPicker')}
          id={this.colorPickerID}
          {...colorPickerProps}
          data-cy={dataCy ? dataCy : undefined}
          {...(ariaLabelledBy && { ariaLabelledBy })}
          ariaDescribedBy={ariaDescribedBy}
          className={'tp-custom-color-picker'}
          data={paletteArray}
          size={size}
          defaultValue={defaultValue || paletteArray[0]}
          value={palette[value] ? palette[value] : this.getColorObject(value)?.length === 2 ? this.getColorObject(value)[1] : undefined}
          disabled={disabled}
          itemRender={this.itemRender}
          leftRightKeysNavigation={false}
          onBlur={this.onBlur}
          onChange={this.onChange}
          name={name}
          popupSettings={{
            ...popupSettings,
            className: `tp-custom-color-picker-menu ${popupSettings?.className}`,
            height: popupSettings?.height || 330,
            width: popupSettings?.width || 200
          }}
          valueRender={this.valueRender.bind(this)}
          ref={el => this.dropDownRef = el}
        />
        {withValidation && <div
          className={'tp-validation-error-message'}
          data-cy={dataCy ? `${dataCy}-error-message` : undefined}
          id={ariaDescribedBy}
        >
          {errorTxt}
        </div>}
      </div>
    );

    if (asField) {
      return (
        <Field {...asFieldProps}>
          {content}
        </Field>
      );
    }

    return content;
  }
}
